import { makeMutable } from "react-native-reanimated";
import type { QueueItem } from "../utils/queue.types";

export type ProgressTarget = {
  songId: string;
  queueItemId: string;
};

/**
 * Progress state using Reanimated shared values.
 *
 * These values update at 100ms intervals and NEVER trigger React re-renders.
 * Components should read from these shared values directly for UI updates.
 *
 * Stream analytics reads directly from these shared values in real-time.
 */
export function createProgressState() {
  const progressState = {
    position: makeMutable(0),
    duration: makeMutable(0),
    buffered: makeMutable(0),
    seekable: makeMutable(true),
    isPositionConfirmed: false,
    // Identity deliberately lives beside the shared values. A position without
    // its queue occurrence is unsafe: the store can switch songs while the UI
    // thread still holds the previous song's timeline.
    target: null as ProgressTarget | null,
  };

  const clampPositionMs = (positionMs: number, durationSeconds?: number) => {
    const safePositionMs = Math.max(0, positionMs);
    if (!durationSeconds || durationSeconds <= 0) return safePositionMs;
    return Math.min(safePositionMs, durationSeconds * 1000);
  };

  function isProgressForPlaybackTarget(item: QueueItem): boolean {
    return (
      progressState.target?.songId === item.metadata.id &&
      progressState.target.queueItemId === item.qid
    );
  }

  function getProgressPositionForQueueItem(
    queueItemId: string | null | undefined,
  ): number | null {
    return queueItemId != null &&
      progressState.target?.queueItemId === queueItemId
      ? progressState.position.value
      : null;
  }

  function getConfirmedProgressPositionForQueueItem(
    queueItemId: string | null | undefined,
  ): number | null {
    return progressState.isPositionConfirmed
      ? getProgressPositionForQueueItem(queueItemId)
      : null;
  }

  function seedProgressForPlaybackTarget(
    item: QueueItem,
    startTimeMs: number = 0,
  ): void {
    // Publish identity first. JS consumers gate the numeric values on this
    // target, so a queue transition can never attribute the old timeline to the
    // newly displayed item.
    progressState.target = {
      songId: item.metadata.id,
      queueItemId: item.qid,
    };
    progressState.isPositionConfirmed = false;
    progressState.position.value =
      clampPositionMs(startTimeMs, item.metadata.duration) / 1000;
    progressState.duration.value = item.metadata.duration ?? 0;
    progressState.buffered.value = 0;
  }

  function ensureProgressForPlaybackTarget(item: QueueItem): void {
    if (!isProgressForPlaybackTarget(item)) {
      seedProgressForPlaybackTarget(item);
    }
  }

  /**
   * Reset all progress state values to initial state.
   * Used during Android warm restarts to clear stale progress values.
   */
  function resetProgressState() {
    progressState.target = null;
    progressState.isPositionConfirmed = false;
    progressState.position.value = 0;
    progressState.duration.value = 0;
    progressState.buffered.value = 0;
    progressState.seekable.value = true;
  }

  return {
    progressState,
    updateNativeProgress: (
      positionMs: number,
      durationMs: number,
      bufferedPositionMs: number,
      isPositionConfirmed: boolean,
    ) => {
      progressState.position.value = positionMs / 1000;
      progressState.isPositionConfirmed = isPositionConfirmed;
      // HLS can report an unknown duration before resolving the stream.
      // Keep the catalog duration until a positive native duration arrives.
      if (durationMs > 0) progressState.duration.value = durationMs / 1000;
      progressState.buffered.value = bufferedPositionMs / 1000;
    },
    isProgressForPlaybackTarget,
    getProgressPositionForQueueItem,
    getConfirmedProgressPositionForQueueItem,
    seedProgressForPlaybackTarget,
    ensureProgressForPlaybackTarget,
    resetProgressState,
  };
}

export type PlayerProgress = ReturnType<typeof createProgressState>;
